import React, { useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { getWindow, trackEvent } from '@surfline/web-common';
import { IconButton, Typography, useMediaQuery } from '@mui/material';

import { ChevronLeft, ChevronRight } from 'components/Icons';
import ZoneCard from 'components/ZoneCard';
import type { TravelZone } from 'types/contentful/content-types';
import { allZonesCardTransformer } from 'utils/transformers';

import Styles from './TravelAllZones.module.scss';

export interface TravelAllZonesProps {
  data: {
    zonesCollection?: { items?: TravelZone[] | undefined } | undefined;
  };
}

export const TravelAllZones: React.FC<TravelAllZonesProps> = ({ data }) => {
  const zones = data?.zonesCollection?.items;
  const totalData = zones?.length;
  const pageSize = 16;

  const [offset, setOffset] = useState(0);
  const [currentData, setCurrentData] = useState(zones ? zones.slice(offset, pageSize) : []);

  const totalPages = Math.ceil(totalData ? totalData / pageSize : 0);
  const currentPage = Math.round(offset / pageSize) + 1;

  const mediumTablet = useMediaQuery('(max-width: 1330px)');
  const mobileLarge = useMediaQuery('(max-width: 976px)');

  const { asPath } = useRouter();

  if (totalData && totalData <= 0) {
    return null;
  }

  const gridItemWidth = () => {
    if (mediumTablet) {
      return 'calc(50% - 20px)';
    }

    return 'calc(25% - 20px)';
  };

  const trackCardClick = (title: string | null | undefined, index: number) => {
    trackEvent('Carousel Clicked', {
      carouselItem: title,
      carouselName: `landing page playlist all destinations`,
      category: 'travel',
      path: asPath,
      positionNumber: index,
      referrer: `${getWindow()?.location?.href ?? ''}${asPath}`,
      search: getWindow().location.search,
      title: 'travel homepage',
      url: `${getWindow()?.location?.href ?? ''}${asPath}`,
    });
  };

  return (
    <section
      style={{
        // @ts-ignore
        '--zones-grid-columns': Math.round(totalData / 4),
        '--zones-grid-item-width': gridItemWidth(),
      }}
      className={classNames(Styles.container, 'sl-section-container')}
    >
      <div className={Styles.header}>
        <div className={Styles.headerInner}>
          <span className={Styles.titleWrap}>
            <Typography variant="title2" className={Styles.title}>
              All Destinations
            </Typography>
          </span>
        </div>
        {!mobileLarge && (
          <div className={Styles.headerButtons}>
            <Typography variant="body" className={Styles.pageCount}>
              Page {currentPage} of {totalPages}
            </Typography>
            <IconButton
              className={Styles.headerButton}
              disableRipple
              data-testid="sl-travel-all-zones-previous-button"
              disabled={offset === 0}
              onClick={() => {
                setCurrentData(zones?.slice(offset - pageSize).slice(0, pageSize) || []);
                setOffset(offset - pageSize);
              }}
              aria-label="Scroll all travel zones left"
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              disableRipple
              style={{ paddingRight: 0 }}
              className={Styles.headerButton}
              data-testid="sl-travel-all-zones-next-button"
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentData(zones?.slice(offset + pageSize).slice(0, pageSize) || []);
                setOffset(offset + pageSize);
              }}
              aria-label="Scroll all travel zones right"
            >
              <ChevronRight />
            </IconButton>
          </div>
        )}
      </div>
      {mobileLarge ? (
        <div className={classNames(Styles.mobileScrollWrap, 'sl-section-full-bleed')}>
          {zones?.map((zone: TravelZone, index: number) => (
            <div key={`item ${index + 1}`} className={Styles.zonesGridItemMobile}>
              <ZoneCard
                data={{
                  ...allZonesCardTransformer(zone),
                  link: `/travel/zones/${zone?.slug}/${zone?.sys?.id}`,
                }}
                trackCardClick={() => trackCardClick(zone?.title, index)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className={Styles.desktopInnerWrap}>
          {currentData?.map((zone: TravelZone, index: number) => (
            <div key={`item ${index + 1}`} className={Styles.zonesGridItem}>
              <ZoneCard
                data={{
                  ...allZonesCardTransformer(zone),
                  link: `/travel/zones/${zone?.slug}/${zone?.sys?.id}`,
                }}
                trackCardClick={() => trackCardClick(zone?.title, index)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default TravelAllZones;
